import { useMemo, useState } from "react";
import {
  ArrowRight,
  Blocks,
  Box,
  Building2,
  FileArchive,
  FileSpreadsheet,
  FileText,
  FolderKanban,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type BudgetKind = "work" | "project";

export type NewBudgetIntentionId =
  | "work-template"
  | "project-template"
  | "ifc-import"
  | "bc3-import"
  | "excel-import"
  | "blank";

export interface NewBudgetSelection {
  intentionId: NewBudgetIntentionId;
  budgetKind: BudgetKind;
  title?: string;
}

interface NewBudgetIntention {
  id: NewBudgetIntentionId;
  title: string;
  description: string;
  icon: typeof FolderKanban;
  fixedKind?: BudgetKind;
  suggestedKind?: BudgetKind;
  requiresKindChoice?: boolean;
  badge?: string;
}

export interface NewBudgetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectIntention?: (selection: NewBudgetSelection) => void;
}

const BUDGET_KIND_LABELS: Record<BudgetKind, string> = {
  work: "Obra",
  project: "Projeto",
};

const INTENTIONS: NewBudgetIntention[] = [
  {
    id: "work-template",
    title: "Modelo de obra",
    description: "Filtra a biblioteca para orçamentos de execução, remodelação e especialidades.",
    icon: Building2,
    fixedKind: "work",
    badge: "Fixo",
  },
  {
    id: "project-template",
    title: "Modelo de projeto",
    description: "Parte de uma proposta técnica com serviços, fases e honorários.",
    icon: FolderKanban,
    fixedKind: "project",
    badge: "Fixo",
  },
  {
    id: "ifc-import",
    title: "Importar IFC",
    description: "Associa um modelo BIM e prepara medições para revisão no editor.",
    icon: Box,
    suggestedKind: "work",
    badge: "BIM",
  },
  {
    id: "bc3-import",
    title: "Importar BC3",
    description: "Converte um ficheiro de troca num orçamento editável com capítulos e partidas.",
    icon: FileArchive,
    requiresKindChoice: true,
  },
  {
    id: "excel-import",
    title: "Importar Excel",
    description: "Transforma uma folha de cálculo existente numa base estruturada para editar.",
    icon: FileSpreadsheet,
    requiresKindChoice: true,
  },
  {
    id: "blank",
    title: "Começar em branco",
    description: "Cria um orçamento vazio quando o trabalho ainda não tem modelo ou ficheiro fonte.",
    icon: FileText,
    requiresKindChoice: true,
  },
];

export function NewBudgetModal({ open, onOpenChange, onSelectIntention }: NewBudgetModalProps) {
  const [selectedIntentionId, setSelectedIntentionId] = useState<NewBudgetIntentionId>("work-template");
  const [title, setTitle] = useState("");
  const [kindByIntention, setKindByIntention] = useState<Partial<Record<NewBudgetIntentionId, BudgetKind>>>({
    "ifc-import": "work",
  });

  const selectedIntention = useMemo(
    () => INTENTIONS.find((intention) => intention.id === selectedIntentionId) ?? INTENTIONS[0],
    [selectedIntentionId],
  );

  const selectedKind =
    selectedIntention.fixedKind ?? kindByIntention[selectedIntention.id] ?? selectedIntention.suggestedKind;

  const canContinue = Boolean(selectedKind);

  const handleKindChange = (intentionId: NewBudgetIntentionId, budgetKind: BudgetKind) => {
    setSelectedIntentionId(intentionId);
    setKindByIntention((current) => ({ ...current, [intentionId]: budgetKind }));
  };

  const handleContinue = () => {
    if (!selectedKind) {
      return;
    }

    onSelectIntention?.({
      intentionId: selectedIntention.id,
      budgetKind: selectedKind,
      title: title.trim() || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92svh] overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="border-b border-border px-5 py-4 text-left sm:px-6">
          <DialogTitle>Novo orçamento</DialogTitle>
          <DialogDescription>
            Escolha a intenção inicial. O tipo Obra/Projeto só aparece quando afeta o fluxo.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[calc(92svh-8rem)] gap-3 overflow-y-auto px-5 py-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3">
          {INTENTIONS.map((intention) => (
            <IntentionCard
              key={intention.id}
              intention={intention}
              isSelected={selectedIntentionId === intention.id}
              selectedKind={kindByIntention[intention.id] ?? intention.suggestedKind}
              onSelect={() => setSelectedIntentionId(intention.id)}
              onKindChange={(budgetKind) => handleKindChange(intention.id, budgetKind)}
            />
          ))}
        </div>

        <div className="border-t border-border px-5 py-4 sm:px-6">
          <label className="grid gap-2 text-sm font-medium">
            Título do orçamento
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Ex.: Remodelação apartamento T2"
            />
          </label>
        </div>

        <DialogFooter className="border-t border-border px-5 py-4 sm:px-6">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {selectedKind ? `${selectedIntention.title} · ${BUDGET_KIND_LABELS[selectedKind]}` : "Escolha Obra ou Projeto para continuar."}
            </p>
            <Button type="button" onClick={handleContinue} disabled={!canContinue} className="gap-2">
              Continuar
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface IntentionCardProps {
  intention: NewBudgetIntention;
  isSelected: boolean;
  selectedKind?: BudgetKind;
  onSelect: () => void;
  onKindChange: (budgetKind: BudgetKind) => void;
}

function IntentionCard({
  intention,
  isSelected,
  selectedKind,
  onSelect,
  onKindChange,
}: IntentionCardProps) {
  const Icon = intention.icon;

  return (
    <article
      className={cn(
        "flex min-h-[190px] flex-col rounded-lg border bg-card p-4 text-card-foreground shadow-xs transition-colors",
        isSelected ? "border-primary ring-2 ring-primary/18" : "border-border hover:border-primary/35",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        {intention.badge ? <Badge variant="secondary">{intention.badge}</Badge> : null}
      </div>

      <div className="mt-4 space-y-1.5">
        <h3 className="text-base font-semibold leading-6">{intention.title}</h3>
        <p className="text-sm leading-5 text-muted-foreground">{intention.description}</p>
      </div>

      <div className="mt-auto pt-4">
        {intention.fixedKind ? (
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline">{BUDGET_KIND_LABELS[intention.fixedKind]}</Badge>
            <Button type="button" variant={isSelected ? "default" : "outline"} size="sm" onClick={onSelect}>
              Selecionar
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2" aria-label={`Tipo para ${intention.title}`}>
              {(["work", "project"] as const).map((budgetKind) => (
                <Button
                  key={budgetKind}
                  type="button"
                  variant={selectedKind === budgetKind && isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => onKindChange(budgetKind)}
                  className="h-8"
                >
                  {BUDGET_KIND_LABELS[budgetKind]}
                </Button>
              ))}
            </div>
            {intention.suggestedKind ? (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Blocks className="h-3.5 w-3.5" aria-hidden="true" />
                Sugerido: {BUDGET_KIND_LABELS[intention.suggestedKind]}
              </div>
            ) : null}
            {intention.requiresKindChoice && !selectedKind ? (
              <p className="text-xs text-muted-foreground">Escolha obrigatória antes de continuar.</p>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
}
